// =============================================================================
// VandieZ admin-web -- cancellation policy types (PR-12, migration 0084)
// =============================================================================
// The charge for cancelling, resolved per (stage, who-cancelled) pair.
// Before 0084 this was two constants in the backend applied at a single
// tier, so changing it meant a release.
// =============================================================================

/** The four FSD p130 lifecycle stages, in lifecycle order. */
export type CancellationStage =
  | "before_assignment"
  | "after_assignment"
  | "after_start"
  | "after_pickup";

/** Who pressed cancel. */
export type CancellationRole = "shipper" | "driver" | "owner" | "admin";

export interface CancellationPolicy {
  id: string;
  stage: CancellationStage;
  cancelled_by_role: CancellationRole;
  /** Fraction of the trip value, e.g. "0.0500" = 5%. */
  rate: string;
  flat_inr: string;
  min_inr: string;
  /** `null` means UNCAPPED -- a deliberate choice, not a missing value. */
  cap_inr: string | null;
  /** False = the charge is shown to the user but never debited. */
  collect: boolean;
  /** Fraction paid to the stranded counterparty. "0" = pure platform fee. */
  counterparty_share: string;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
  created_at: string;
  created_by_admin_id: string | null;
  created_by_name: string | null;
}

export interface CancellationPolicyListResponse {
  items: CancellationPolicy[];
  total: number;
  /**
   * The server's own vocabulary. The form builds its dropdowns from
   * these rather than a hardcoded client copy, so adding a stage
   * backend-side doesn't leave this page silently unable to target it.
   */
  stages: CancellationStage[];
  roles: CancellationRole[];
}

export interface CancellationPolicyCreatePayload {
  stage: CancellationStage;
  cancelled_by_role: CancellationRole;
  rate: string;
  flat_inr?: string;
  min_inr?: string;
  /** Omit or `null` for uncapped. */
  cap_inr?: string | null;
  /** Turn money movement off for this pair without a deploy. */
  collect?: boolean;
  /** "0" = pure fee, "1" = pure compensation for the stranded party. */
  counterparty_share?: string;
  /** ISO timestamp. Omit for "now"; a future value stages the change. */
  effective_from?: string;
  notes?: string;
}

/** Human labels, in lifecycle order rather than alphabetical. */
export const STAGE_LABEL: Record<CancellationStage, string> = {
  before_assignment: "Before a bid is accepted",
  after_assignment: "After assignment, before the driver starts",
  after_start: "After the driver starts",
  after_pickup: "After pickup / loading",
};

export const STAGE_HINT: Record<CancellationStage, string> = {
  before_assignment:
    "Nobody has committed yet. Free today — charging here discourages posting.",
  after_assignment:
    "A driver has committed but not moved. Costs them time, not fuel.",
  after_start:
    "The driver is en route to the pickup. Fuel and time are already spent.",
  after_pickup:
    "The goods are loaded. The shipper has stopped looking for anyone else.",
};

export const ROLE_LABEL: Record<CancellationRole, string> = {
  shipper: "Shipper cancels",
  driver: "Driver cancels",
  owner: "Fleet owner cancels",
  admin: "Admin cancels",
};
